import type {SelectOptionItem} from '@logistics-frontend/blocks'
import {get} from '@logistics-frontend/utils'
import {dateWithTimeZone} from '@logistics-frontend/utils/timezone'
import {useFormContext} from '@yandex-delivery/form'
import type {DeliveryInterval} from '@yandex-delivery/form/ui/Offers/delivery-intervals/type'
import {useMemo} from 'react'

import type {DueSettings} from 'src/shared/types'

export function useOrderTime(name: string, dueSettings: DueSettings) {
  const {watch} = useFormContext()
  const deliveryDate: string | undefined = watch(`${name}.deliveryDate`)

  // Получаем период и границы даты
  const period = get('period', dueSettings)
  const maxDate = dateWithTimeZone(dueSettings.to)

  const timeIntervals = useMemo(() => {
    let currentDate = dateWithTimeZone(dueSettings.from)

    const result = new Map<string, SelectOptionItem<DeliveryInterval>[]>()

    // Итерируемся через каждый слот времени между минимальной и максимальной датой
    while (currentDate.isBefore(maxDate)) {
      // Изменил на while для удобства, но можно оставить и for
      const dateFormatted = currentDate.format('DD.MM.YYYY')

      // Добавляем слот времени к соответствующей дате в результате
      const timeSlot = {
        label: currentDate.format('HH:mm'),
        value: {
          from: currentDate.toISOString(),
          to: currentDate.toISOString(),
        },
      }

      if (result.has(dateFormatted)) {
        result.get(dateFormatted)?.push(timeSlot)
      } else {
        result.set(dateFormatted, [timeSlot])
      }

      // Обновляем currentDate, присваивая ей новое значение потому, что DayJS не мутирует объект даты, а возвращает новый
      currentDate = currentDate.add(period, 'minutes')
    }

    return result
  }, [dueSettings.from, maxDate, period])

  const dateList = useMemo(() => {
    return [...timeIntervals.keys()].map(date => ({
      label: date,
      value: date,
    }))
  }, [timeIntervals])

  const intervalList = useMemo(() => {
    if (deliveryDate === undefined) {
      return []
    }

    const intervals = timeIntervals.get(deliveryDate)

    if (intervals === undefined) {
      return []
    }

    return intervals
  }, [deliveryDate, timeIntervals])

  return {
    deliveryDate,
    dateList,
    intervalList,
  } as const
}
